/**
 * app-configuration-response.ts
 * Copyright (c) 2017, My Coverage Plan, Inc.
 * All rights reserved.
 */

import { validateAs, validateFor } from "../../validation/decorators";
import { IncomingMessage } from "../general/incoming-message";
import { MessageTag } from "../message-tag";

/**
 * The format of the configuration data for each zone. This should be roughly
 * equivalent to the data on a {@link ZoneConfigurationResponse}, barring some
 * permissions and debug constraints.
 */
export interface ZoneConfig
{
    organizationName : string;
    url : string;
    title : string;
    supportRequestEnabled : boolean;
		hasCustomSupport : boolean;
		targetSupportGroup : string | null;
    referralsEnabled : boolean;
		hasCustomReferralRequester : boolean;
		targetReferralGroup : string | null;
    defaultLanguage : string;
    defautCountry : string;
		minimumSecurePromptCount : number;
		adminZone : boolean;
		groups : string[];
    algorithms : string[];
		aggregations : any[];
		zoneId : number;
		zoneUUID : string;
    trustedConnections: string[];
}

/**
 * The format for serialization entries within an index.
 */
export interface SerializationIndex
{
    name : string;
    index : number;
}

export interface RuleConfig
{
    name : string;
    description : string;
    isGroupRelative : boolean;
}

export interface JarInformation {
  jarName: string;
  jarPath: string;
  classCount: number;
  fileModifiedDate: number;
  lastEntryModifiedDate: number;
  fileSize: number;
  md5Hash: string;
}

/**
 * The {@link AppConfigurationResponse} carries details about the application's
 * configuration for display in the admin panel. Note that these values are
 * static relative to the app's lifetime, contrasting with application status
 * metadata, which can be reconfigured and change over a lifetime (or session).
 */
@validateFor(MessageTag.RSP_CFG_APP)
export class AppConfigurationResponse extends IncomingMessage
{
    public tag : MessageTag = MessageTag.RSP_CFG_APP;

    /**
     * Whether the app is running in a development environment.
     */
    @validateAs("boolean")
    public developmentMode : boolean;

    /**
     * A list of emails that are notified when errors occur.
     */
    @validateAs({array: "string"})
    public errorNotificationEmails : string[];

    /**
     * Location where filter class files are exported.
     */
    @validateAs("string")
    public filterLocation : string;

    /**
     * What port the server is listening on.
     */
    @validateAs(["integer", {min: 1}, {max: 65535}])
    public listenPort : number;

    /**
     * The hostname of the channel (either domain or IP)
     */
    @validateAs("string")
    public listenHost : string;

    /**
     * List of JAR files included in the server config.
     */
    @validateAs({array: "object"})
    public jars : JarInformation[];

    /**
     * List of roles provided by the system security, and the messages they
     * each permit usage of.
     */
    @validateAs({array: "object"}, true)
    public roles : any[]; // TODO: deprecated!

    /**
     * List of the rule names and descriptions for system permissions.
     */
    @validateAs({array: "object"}) // TODO: better validation
    public rules : RuleConfig[];

    /**
     * List of configured zones for the application.
     */
    @validateAs({array: "object"}) // TODO: better validation
    public zones : ZoneConfig[];

    @validateAs("object") // TODO: better validation
    public indices : {
        algorithmIndices : SerializationIndex[];
        algorithmStepIndices : SerializationIndex[];
        stateVariableIndices : SerializationIndex[];
        countryCodeIndices : SerializationIndex[];
        languageCodeIndices : SerializationIndex[];
    };

}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/server/messages/admin/app-configuration-response.ts